import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/config';
import { clearNativeToken, getNativeToken, setNativeToken } from '@/lib/native';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    let retrying = false;
    try {
      const nativeToken = await getNativeToken();
      let response;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          response = await fetch(`${BACKEND_URL}/api/auth/me`, {
            headers: nativeToken ? { Authorization: `Bearer ${nativeToken}` } : {},
            credentials: 'include',
            signal: AbortSignal.timeout(8000)
          });
          if (response.status === 401 || response.status === 403) break;
          if (response.ok) break;
        } catch (error) {
          if (attempt === 2) throw error;
        }
        await new Promise(resolve => setTimeout(resolve, 700 * (attempt + 1)));
      }
      if (!response?.ok) {
        const authRejected = response?.status === 401 || response?.status === 403;
        if (authRejected) {
          setIsAuthenticated(false);
          setUser(null);
          return;
        }
        throw new Error('Conexão temporariamente indisponível');
      }
      const userData = await response.json();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      // Uma queda do Cloudflare ou da rede não apaga a sessão nativa. O token
      // continua protegido no aparelho e uma nova tentativa acontece ao reabrir.
      const nativeToken = await getNativeToken();
      if (nativeToken) {
        retrying = true;
        setLoading(true);
        window.setTimeout(checkAuth, 2500);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } finally {
      if (!retrying) setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (userData, accessToken = null) => {
    if (accessToken) await setNativeToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      const nativeToken = await getNativeToken();
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers: nativeToken ? { Authorization: `Bearer ${nativeToken}` } : {},
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    await clearNativeToken();
    setUser(null);
    setIsAuthenticated(false);
  };

  const authFetch = useCallback(async (url, options = {}) => {
    const headers = new Headers(options.headers || {});
    const nativeToken = await getNativeToken();
    if (nativeToken) headers.set('Authorization', `Bearer ${nativeToken}`);
    // Corpos já serializados precisam ser identificados como JSON. FormData
    // fica sem este cabeçalho para o navegador gerar o boundary do upload.
    if (typeof options.body === 'string' && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    // Dentro do APK, caminhos iniciados por / apontariam para o servidor local
    // do WebView. Centralizamos aqui para que chat, perfil, feed, notificações
    // e uploads sempre conversem com a API pública do BlopBoru.
    const targetUrl = typeof url === 'string' && url.startsWith('/')
      ? `${BACKEND_URL}${url}`
      : url;
    return fetch(targetUrl, { ...options, headers, credentials: 'include' });
  }, []);

  const getMediaUrl = useCallback((path) => {
    return `${BACKEND_URL}/api/files/${path}`;
  }, []);

  const value = {
    user,
    setUser,
    isAuthenticated,
    loading,
    login,
    logout,
    authFetch,
    getMediaUrl
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
